import { useContext, useEffect, useState } from 'react'

import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Instrument } from '@cash-web/protos/squareup/franklin/api/instrument.pb'
import { PasscodeCreationBlocker } from '@cash-web/protos/squareup/franklin/blockers/passcode_blockers.pb'
import { Profile } from '@cash-web/protos/squareup/franklin/ProfileProto.pb'
import { PasscodeCreationBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { SetPasscodeResponseStatus } from '@cash-web/protos/squareup/franklin/SetPasscodeResponseProto.pb'
import { useAuth } from '@cash-web/shared-auth-data-access-context'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSetPasscode } from '@cash-web/shared-data-access-api/react-query/useMutationSetPasscode'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BlockerLoading } from '../../BlockerLoading/BlockerLoading'
import { getPasscodeLength } from '../BlockerPasscodeVerification/getPasscodeLength'
import { BlockerPasscodeStep } from './BlockerPasscodeStep'

export type BlockerPasscodeCreationProps = {
  blocker?: PasscodeCreationBlocker
  supplement?: PasscodeCreationBlockerSupplement
}
export type BlockerPasscodeCreationFormFields = {
  old_passcode?: string
  confirmation_passcode?: string
  new_passcode?: string
}

const defaultSteps = ['currentPasscode', 'newPasscode', 'confirmPasscode'] as const
const emptyWeakPasscodes: string[] = []

const getVerficationInstrument = (profile: Profile): Instrument => {
  const token = profile?.verification_instrument_token
  return {
    ...profile?.instruments?.find(instrument => instrument.token),
    token,
  }
}

export const BlockerPasscodeCreation = ({ blocker, supplement }: BlockerPasscodeCreationProps) => {
  // reValidateMode 'onSubmit': after a failed submit (e.g. a weak or incomplete PIN), the default
  // per-keystroke revalidation would surface submit-time errors like "4 characters are expected."
  // while the user is mid-entry on a fresh attempt. PIN rules are only meaningful for a complete
  // entry, so they re-run on the next submit instead.
  const form = useForm({ mode: 'onTouched', reValidateMode: 'onSubmit' })
  const { profile = {}, isLoading: isProfileLoading } = useAuth()
  const { t } = useTranslation()
  const { setError, clearError } = useContext(ErrorContext)
  const weakPinClientCheckEnabled = useFeatureFlag('cash-web-weak-pin-client-check') === true
  // The server sends the weak-PIN list only when blocking is active for this customer; when the
  // list is empty or absent the client must not block locally (see
  // PasscodeCreationBlockerSupplement.weak_passcodes). Server-side rejection remains the backstop.
  const weakPasscodes = supplement?.weak_passcodes ?? emptyWeakPasscodes
  const weakPinCheckActive = weakPinClientCheckEnabled && weakPasscodes.length > 0
  const flow = useFlowContext()
  const isExistingPasscode = blocker?.requires_existing_passcode ?? false
  const [steps, setSteps] = useState(isExistingPasscode ? defaultSteps : defaultSteps.slice(1))

  const step = steps[0]

  const { mutateAsync: setPasscode } = useMutationSetPasscode({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })
  const verificationInstrument =
    step !== 'currentPasscode' ? { token: 'CUSTOMER_PASSCODE' } : getVerficationInstrument(profile)
  const isCustomerPasscode =
    verificationInstrument.token === 'CUSTOMER_PASSCODE' || verificationInstrument.token === 'CASH_BALANCE'
  const canResetPasscode = isCustomerPasscode && step === 'currentPasscode'

  const { handleBlockerResponse, genericError } = useBlockerResponseHandler()

  const formValues = form.getValues()
  const handleEnterPinSubmit = () => {
    clearError()
    let didAdvance = false
    form
      .handleSubmit(() => {
        didAdvance = true
        setSteps(steps.slice(1))
      })()
      .then(() => {
        // Reset the form when transitioning between steps since it should act as a new form.
        // Only on a successful transition: resetting after a failed validation (e.g. a weak PIN
        // rejected by the validatePin rule) would restore the just-cleared entry and wipe the
        // inline error. The reset must also stay out of handleSubmit's callback, which runs
        // before RHF marks the form submitted and would clobber keepIsSubmitted: false.
        if (didAdvance) {
          form.reset({ ...formValues, confirmation_passcode: '' }, { keepIsSubmitted: false })
        }
      })
  }

  const newPasscode = useWatch({ control: form.control, name: 'new_passcode' })
  const confirmationPasscode = useWatch({
    control: form.control,
    name: 'confirmation_passcode',
  })
  const passcodeMismatch = newPasscode !== confirmationPasscode

  // A local rejection skips the submit handlers, so clear any blocker-level error still on
  // screen (for example, the mismatch banner) from an effect rather than from validation.
  useEffect(() => {
    if (weakPinCheckActive && typeof newPasscode === 'string' && weakPasscodes.includes(newPasscode)) {
      clearError()
    }
  }, [clearError, newPasscode, weakPinCheckActive, weakPasscodes])

  const handleConfirmPinSubmit = () => {
    clearError()
    form
      .handleSubmit(handleConfirmSubmitAction)()
      .then(() => {
        if (passcodeMismatch) {
          // Reset the form when transitioning between steps but maintain old passcode information
          form.reset({ old_passcode: formValues['old_passcode'] }, { keepIsSubmitted: false })
          setError(
            t('Your PINs don’t match. Try again.', {
              context: 'error message',
            })
          )
          setSteps(defaultSteps.slice(1))
        }
      })
  }

  const handleConfirmSubmitAction = async (data: BlockerPasscodeCreationFormFields) => {
    if (passcodeMismatch) {
      return
    }
    const response = await handleBlockerResponse({
      promisedBlockerUpdate: setPasscode({
        old_passcode: data.old_passcode,
        old_instrument_token: verificationInstrument.token,
        new_passcode: data.new_passcode,
        transfer_token: flow.active?.transferToken,
        payment_tokens: flow.active?.paymentTokens,
      }),
      errors: {
        CONCURRENT_MODIFICATION: genericError,
        INVALID: t('Invalid Cash PIN. Please try again.', {
          context: 'Error to the user when the response status is INVALID',
        }),
        NONRETRYABLE_FAILURE: genericError,
        RETRYABLE_FAILURE: genericError,
      },
      onFailure(response) {
        if (!response) return
        if (response.status === SetPasscodeResponseStatus.RETRYABLE_FAILURE) {
          setSteps(defaultSteps)
        }
      },
    })
    // The server rejects a disallowed PIN (e.g. one that is too weak) with a SUCCESS status,
    // the reason in dialog_message, and the blocker re-issued under the same id, so this
    // component is not remounted — restart from the new PIN step and surface the reason
    // ourselves.
    const dialogMessage = response?.response_context?.dialog_message
    if (response?.status === SetPasscodeResponseStatus.SUCCESS && dialogMessage) {
      form.reset({ old_passcode: data.old_passcode }, { keepIsSubmitted: false })
      setError(dialogMessage)
      setSteps(defaultSteps.slice(1))
    }
  }

  // Client-side counterpart of the server's weak-PIN check, run at the first PIN screen so the
  // user gets immediate feedback (shake + inline error) instead of a round trip and reprompt.
  const validateNewPasscode = (pin: string) => {
    if (!weakPasscodes.includes(pin)) {
      return undefined
    }
    return t('Choose a stronger PIN', {
      context: 'inline error shown when the new PIN the user entered is too weak',
    })
  }

  if (isProfileLoading) {
    return <BlockerLoading />
  }

  switch (step) {
    case 'currentPasscode':
      return (
        <BlockerPasscodeStep
          name="old_passcode"
          mainText={t('Enter your current PIN', { context: 'title' })}
          // Subtitles only render with the flag on: BlockerPasscodeStep historically dropped
          // secondaryText, so showing them unconditionally would change the flag-off UI.
          secondaryText={
            weakPinCheckActive
              ? t("To change your PIN, you'll need to first enter your current PIN.", {
                  context: 'subtitle',
                })
              : undefined
          }
          skippable={supplement?.skippable}
          onSubmit={handleEnterPinSubmit}
          form={form}
          canResetPasscode={canResetPasscode}
          length={getPasscodeLength(verificationInstrument)}
        />
      )
    case 'newPasscode':
      return (
        <BlockerPasscodeStep
          name="new_passcode"
          mainText={
            weakPinCheckActive ? t('Create a PIN', { context: 'title' }) : t('Create a new PIN', { context: 'title' })
          }
          // The weak-PIN guidance copy is server-sent (prompt_sub_text, set alongside
          // weak_passcodes) so the wording stays consistent across platforms and can be
          // localized/tuned without a client release.
          secondaryText={weakPinCheckActive ? supplement?.prompt_sub_text : undefined}
          skippable={supplement?.skippable}
          onSubmit={handleEnterPinSubmit}
          form={form}
          canResetPasscode={canResetPasscode}
          length={getPasscodeLength(verificationInstrument)}
          validatePin={weakPinCheckActive ? validateNewPasscode : undefined}
        />
      )
    case 'confirmPasscode':
      return (
        <BlockerPasscodeStep
          name="confirmation_passcode"
          mainText={t('Confirm your new PIN', { context: 'title' })}
          secondaryText={
            weakPinCheckActive
              ? t('Type your new PIN again to confirm.', {
                  context: 'subtitle',
                })
              : undefined
          }
          skippable={supplement?.skippable}
          onSubmit={handleConfirmPinSubmit}
          form={form}
          canResetPasscode={canResetPasscode}
          length={getPasscodeLength(verificationInstrument)}
        />
      )
  }
}
